import * as React from 'react';
import PipeIcon from 'mdi-material-ui/Pipe';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import {
  ClientEmployeeOQMendixPageId,
  ClientContractorOQMendixPageId,
  ContractorOQMendixPageId
} from '@pec/aion-ui-core/enums/MendixPageIds';
import { DeepReadonly } from 'utility-types';
import { SectionContainer } from '../../containers/Nav/Section';
import { AnchorListItem } from './AnchorListItem';
import { useTranslation } from 'react-i18next';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';

export type Props = {
  organization: DeepReadonly<IOrganization>;
  client: boolean;
};

export const VerisourceOQNavigation: React.FC<Props> = ({ organization, client }) => {
  const { id: organizationId } = organization;
  const { t } = useTranslation();

  return (
    <SectionContainer
      id="VerisourceOQNavigation"
      tooltip
      subheader={t('components.verisourceOqNavigation.operatorQualifications', 'Operator Qualifications')}
      icon={<PipeIcon />}
      hasNotifications={false}
    >
      {organization.features.includes(OrganizationFeature.VerisourceContractorOQNavigation) &&
        (client ? (
          <FeatureFlagWrapper>
            <Feature
              name={'HideLinkVeriforceCompany'}
              render={(isEnabled: boolean) => (
                <React.Fragment>
                  <AnchorListItem>
                    <b>{t('components.verisourceOqNavigation.contractorOq', 'Contractor OQ')}</b>
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientContractorOQMendixPageId.VendorReport}`}
                  >
                    {t('components.verisourceOqNavigation.vendorReport', 'Vendor Report')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientContractorOQMendixPageId.TaskList}`}
                  >
                    {t('components.verisourceOqNavigation.taskList', 'Task List')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientContractorOQMendixPageId.FieldVerificationReport}`}
                  >
                    {t('components.verisourceOqNavigation.fieldVerificationReport', 'Field Verification Report')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientContractorOQMendixPageId.Qualifications}`}
                  >
                    {t('components.verisourceOqNavigation.qualifications', 'Qualifications')}
                  </AnchorListItem>
                </React.Fragment>
              )}
            />
          </FeatureFlagWrapper>
        ) : (
          <FeatureFlagWrapper>
            <Feature
              name={'HideLinkVeriforceCompany'}
              render={(isEnabled: boolean) => (
                <React.Fragment>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.ActiveEmployees}`}
                  >
                    {t('components.verisourceOqNavigation.activeEmployees', 'Active Employees')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.QualifiedEmployees}`}
                  >
                    {t('components.verisourceOqNavigation.qualifiedEmployees', 'Qualified Employees')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.FieldVerificationReport}`}
                  >
                    {t('components.verisourceOqNavigation.fieldVerificationReport', 'Field Verification Report')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.ExpiringQualifications}`}
                  >
                    {t('components.verisourceOqNavigation.expiringQualifications', 'Expiring Qualifications')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.PortIndividuals}`}
                  >
                    {t('components.verisourceOqNavigation.portIndividuals', 'Port Individuals')}
                  </AnchorListItem>
                  <AnchorListItem
                    inset
                    href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ContractorOQMendixPageId.QRExport}`}
                  >
                    {t('components.verisourceOqNavigation.qrExport', 'QR Export')}
                  </AnchorListItem>
                </React.Fragment>
              )}
            />
          </FeatureFlagWrapper>
        ))}
      ,
      {organization.features.includes(OrganizationFeature.VerisourceEmployeeOQNavigation) && client && (
        <FeatureFlagWrapper>
          <Feature
            name={'HideLinkVeriforceCompany'}
            render={(isEnabled: boolean) => (
              <React.Fragment>
                <AnchorListItem>
                  <b>{t('components.verisourceOqNavigation.employeeOq', 'Employee OQ')}</b>
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.TaskList}`}
                >
                  {t('components.verisourceOqNavigation.taskList', 'Task List')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.EmployeeList}`}
                >
                  {t('components.verisourceOqNavigation.employeeList', 'Employee List')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.Assignments}`}
                >
                  {t('components.verisourceOqNavigation.assignments', 'Assignments')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.Qualifications}`}
                >
                  {t('components.verisourceOqNavigation.qualifications', 'Qualifications')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.ExpiringQualifications}`}
                >
                  {t('components.verisourceOqNavigation.expiringQualifications', 'Expiring Qualifications')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.FieldVerificationReport}`}
                >
                  {t('components.verisourceOqNavigation.fieldVerificationReport', 'Field Verification Report')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.EmployeeHistory}`}
                >
                  {t('components.verisourceOqNavigation.employeeHistory', 'Employee History')}
                </AnchorListItem>
                <AnchorListItem
                  inset
                  href={isEnabled ? undefined : `/veriforce-integration/${organizationId}?RedirectUrl=/VS/start.aspx?mx=${ClientEmployeeOQMendixPageId.QRExport}`}
                >
                  {t('components.verisourceOqNavigation.qrExport', 'QR Export')}
                </AnchorListItem>
              </React.Fragment>
            )}
          />
        </FeatureFlagWrapper>
      )}
    </SectionContainer>
  );
};
